// @flow

import alt from '../alt';

import ProductActions from '../actions/ProductActions';
import SettingsActions from '../actions/SettingsActions';
import NotificationActions from '../actions/NotificationActions';
import SettingsStore from './SettingsStore';
import ProductSource from '../sources/ProductSource';

import ProductInfo from '../ProductInfo';
import GrFileParser from '../GrFileParser';
import NcpFileParser from '../NcpFileParser';
import DatFileParser from '../DatFileParser';
import _ from 'underscore';
import t from '../trans';
import ProductTransformer from "../ProductTransformer";
import SchemaFactory from "../SchemaFactory";
import type {AltStore, StoreModel} from "../alt";

/**
 * Tooteinfo haldamise store
 */
class ProductStore implements StoreModel<ProductInfo> {
    state: ProductInfo;
    schema: {...};

    constructor() {
        this.state = _.clone(this.getInitialState());
        this.bindActions(ProductActions);
        this.bindActions(SettingsActions);
        this.exportAsync(ProductSource);

        let schemaFactory = SchemaFactory.getInstance();
        this.schema = schemaFactory.getSchema("product");
    }

    onSetSchema(schema: {...}): void {
        this.schema = schema;
    }

    readProductData(info: {code: string, type: ?string}): void {
        ProductActions.fetchNcp.defer(info.code, info.type);
    }

    saveProductData(code: string): void {
        let newState = {
            'grFailiNimi': this.state.kood + '.gr2'
        };
        if (this.state.ncpData) {
            newState['failiNimi'] = this.state.kood + '.' + this.state.ncpType;
            ProductActions.saveNcp.defer(code);
        }
        ProductActions.saveProduct.defer(code);
        this.setState(newState);
    }


    /**
     * Toote laadimise käivitamine
     */
    onFetchProduct(code: string): void {
        let p = this.getInstance().fetchProduct(code);
        // We need to catch the exception - this is already processed but if we do not catch this we receive warning in console
        p.catch(function () {});
    }

    /**
     * Toote laadimine ebaõnnestus
     */
    onFetchProductFailed(error) {
        if (error.response.status == 404) {
            NotificationActions.error.defer(t('%s data not found', "GR2"));
        } else {
            this.showError(error);
        }
    }

    showError(error) {
        let message = t('Server error');
        if (error.response) {
            if (error.response.data && error.response.data.error && error.response.data.error.message) message = error.response.data.error.message;
            else {
                switch (error.response.status) {
                    case 404:
                        message = t("Product was not found");
                        break;
                    default:
                        message = t("Server error");
                }
            }
        }
        NotificationActions.error.defer(message);
    }

    /**
     * Toode sai laaditud
     */
    onFetchProductDone(response) {
        // TODO: Toote andmete sisselaadimine
        try {
            if (!response.data.productsResult) throw t("Server error");
            let transformer = new ProductTransformer(this.state);
            let d = transformer.glassProductToState(response.data.productsResult);
            delete d.glass;
            if (this.state.lineCount != d.lineCount) throw t("Line count does not match to current line count");
            let newState = this.cloneState();
            newState.extend(d);
            newState.grFailiNimi = newState.kood + '.gr2';
            if (newState.keeratud !== this.state.keeratud) {
                newState.glass.rotate180();
            }
            if (newState.peegeldatud && !this.state.peegeldatud) {
                newState.glass.mirror();
            }
            if (newState.rotated !== this.state.rotated) {
                newState.glass.rotate90();
            }
            newState.calcGlass();
            //newState.calcKoord();
            this.recalcPointsAngle(newState);
            this.setState(_.clone(newState));
            NotificationActions.success.defer(t('%s data loaded', "GR2"));
        } catch (e) {
            if (!_.isString(e)) {
                console.log(e.toString());
                NotificationActions.error.defer(t("Server error"));
            }
            else NotificationActions.error.defer(e);
        }
    }

    /**
     * Toote salvestamine
     */
    onSaveProduct() {
        let p = this.getInstance().saveProduct();
        // We need to catch the exception - this is already processed but if we do not catch this we receive warning in console
        p.catch(function () {});
    }

    /**
     * Toote salvedstamine ebaõnnestus
     */
    onSaveProductFailed(error) {
        this.showError(error);
    }

    onSaveProductDone() {
        NotificationActions.success.defer(t('Product saved'));
    }

    onDeleteProduct(code) {
        let p = this.getInstance().deleteProduct(code);
        // We need to catch the exception - this is already processed but if we do not catch this we receive warning in console
        p.catch(function () {});
    }

    onDeleteProductFailed(error) {
        this.showError(error);
    }

    onDeleteProductDone() {
        this.setInitialState();
        NotificationActions.success.defer(t('Product deleted'));
    }

    onFetchNcp(info) {
        if (info.type == null) info.type = this.getNextGlassFileType();
        let newState = this.cloneState();
        newState.kood = info.code;
        newState.ncpType = info.type;
        this.setState(_.clone(newState));
        let p = this.getInstance().fetchNcp(info.code, info.type);
        // We need to catch the exception - this is already processed but if we do not catch this we receive warning in console
        p.catch(function () {});
    }

    onFetchNcpFailed(error) {
        // Kui feilas, proovime järgmist failitüüpi:
        let ncpType = this.getNextGlassFileType(this.state.ncpType);
        if (ncpType != null) {
            ProductActions.fetchNcp.defer(this.state.kood, ncpType);
            return;
        }

        // Vaatamata veale loeme tooteinfo sisse:
        ProductActions.fetchProduct.defer(this.state.kood);
        if (error.response.status == 404) {
            NotificationActions.error.defer(t('%s data not found', "NCP"));
        } else {
            this.showError(error);
        }
    }

    getNcpFileTypes() {
        let types = SettingsStore.getGlassFileType();
        return _.map(types.split(","), function (v) {
            return v.trim();
        });
    }

    getNextGlassFileType(fileType) {
        let fileTypes = this.getNcpFileTypes();
        for (let i = 0; i < fileTypes.length; i++) {
            if (fileType == null) return fileTypes[i];
            else if (fileType == fileTypes[i]) {
                fileType = null;
            }
        }
        return null;
    }

    onFetchNcpDone(response) {
        let rotated = SettingsStore.isNcpRotated();
        let kood = this.state.kood;
        let ncpType = this.state.ncpType;
        try {
            let newState = this.getInitialState();

            newState.ncpData = response.data;
            let ncpParser = this.createNcpFileParser(rotated, ncpType);
            newState.glass.loadGlassData(ncpParser.parse(response.data), rotated);
            newState.calcGlass();
            newState.failiNimi = kood + '.' + ncpType.toLowerCase();
            newState.calcKoord();
            this.recalcPointsAngle(newState);
            newState.kood = kood;
            newState.peegeldatud = newState.glass.peegeldatud;
            newState.keeratud = newState.glass.keeratud;
            newState.rotated = newState.glass.rotated;
            this.setState(_.clone(newState));
            NotificationActions.success.defer(t('%s data loaded', ncpType.toUpperCase()));
        } catch (e) {
            if (!_.isString(e)) {
                console.log(e.toString());
                NotificationActions.error.defer(t("Invalid %s data", ncpType.toUpperCase()) + " " + e.toString());
            }
            else NotificationActions.error.defer(e);
        }
        // Loeme tooteinfo kah sisse:
        ProductActions.fetchProduct.defer(kood);
    }

    onSaveNcp() {
        let p = this.getInstance().saveNcp();
        // We need to catch the exception - this is already processed but if we do not catch this we receive warning in console
        p.catch(function () {});
    }

    onSaveNcpFailed(error) {
        this.showError(error);
    }

    onSaveNcpDone() {
        NotificationActions.success.defer(t('%s data saved', this.state.ncpType.toUpperCase()));
    }

    onCalcPoints() {
        let newState = this.cloneState();
        newState.calcJoonAY();
        newState.calcE();
        newState.calcP();
        newState.calcLengthE();
        newState.calcOffsetP();
        newState.calcOffsetHud();
        newState.calcKoord();
        this.setState(_.clone(newState));
    }

    getInitialState() {
        let productConfig = SettingsStore.getProductConfig();
        return new ProductInfo(productConfig);
    }

    setInitialState() {
        this.setState(_.clone(this.getInitialState()));
    }

    cloneState() {
        return new ProductInfo(this.state);
    }

    onSettingsUpdated(settings) {
        this.waitFor(SettingsStore);
        let productConfig = SettingsStore.getProductConfig();
        if (!productConfig.isEqual(this.state)) this.setInitialState();
    }

    /**
     * Parsib faili sisse ning väärtustab andmed
     */
    onUploadNcpFile(info) {
        /*
         * Call loefail(failinimi)
         * Vajalikud tegevused:
         Call arvuta_e
         Call arvuta_p
         Call koordinaadid
         Call joonista - tehakse Product.js failis!
         */
        let rotated = SettingsStore.isNcpRotated();
        try {
            let newState = this.getInitialState();
            newState.ncpData = info.data;
            newState.ncpType = info.ncpType;
            let ncpParser = this.createNcpFileParser(rotated, info.ncpType);
            newState.glass.loadGlassData(ncpParser.parse(info.data), rotated);
            newState.calcGlass();
            newState.failiNimi = info.name;
            newState.calcKoord();
            this.recalcPointsAngle(newState);
            this.setState(_.clone(newState));
        } catch (e) {
            if (!_.isString(e)) {
                console.log(e.toString());
                NotificationActions.error.defer(t("Invalid %s file", info.ncpType.toUpperCase()) + " '" + info.name + "': " + e.toString());
            }
            else NotificationActions.error.defer(e);
        }
    }

    /**
     * GR2 faili laadimine
     * @param {object} info GR2 faili info objekt, selles peavad leiduma väljad "data" (GR2 faili struktuur) ja "name" (faili nimi)
     */
    onUploadGrFile(info) {
        /*
         * Call loegrtfail(sisend_grt)
         * Call arvuta_e
         * 		arvuta_p
         * 		koordinaadid
         * Call koordinaadid
         * 		arvuta_Kalle
         */
        try {
            let p = new GrFileParser();
            let gp = p.parse(info.data);
            let transformer = new ProductTransformer(this.state);
            let d = transformer.glassProductToState(gp);
            // Eemaldame glass info:
            delete d.glass;
            // Arvutame Pi, neid GR2 failis pole.
            d.calcPi();
            // if (!_.isString(this.state.failiNimi) || this.state.failiNimi.length == 0) throw "No NCP file opened";
            if (this.state.lineCount != d.lineCount) throw t("Line count does not match to current line count");
            let newState = this.cloneState();
            newState.extend(d);
            newState.grFailiNimi = info.name;
            if (newState.keeratud !== this.state.keeratud) {
                newState.glass.rotate180();
            }
            if (newState.peegeldatud && !this.state.peegeldatud) {
                newState.glass.mirror();
            }
            if (newState.rotated && !this.state.rotated) {
                newState.glass.rotate90();
            }
            newState.calcGlass();
            //newState.calcKoord();
            this.setState(_.clone(newState));
        } catch (e) {
            if (_.isString(e)) NotificationActions.error.defer(e);
            else {
                console.log(e.toString());
                NotificationActions.error.defer("Invalid GR2 file");
            }
        }
    }

    /**
     * Joone peegeldamine
     */
    onMirror() {
        let newState = this.cloneState();
        newState.mirror();
        // Kallet ei arvuta:
        newState.calcKoord(false);
        newState.copyKalle();
        this.setState(_.clone(newState));
    }

    /**
     * Klaasi peegeldamine
     */
    onMirrorShape() {
        let newState = this.cloneState();
        newState.glass.mirror();
        newState.peegeldatud = newState.glass.peegeldatud;
        newState.calcE();
        newState.calcP();
        newState.calcKoord();
        this.setState(_.clone(newState));
    }

    /**
     * Klaasi keeramine 180 kraadi
     */
    onRotate() {
        let newState = this.cloneState();
        newState.glass.rotate180();
        newState.keeratud = newState.glass.keeratud;
        newState.calcE();
        newState.calcP();
        newState.calcKoord();
        this.setState(_.clone(newState));
    }

    /**
     * Klaasifaili sisu keeramine 90 kraadi
     */
    onRotate90() {
        let newState = this.cloneState();
        newState.glass.rotate90();
        newState.rotated = newState.glass.rotated;
        newState.calcJoonAY();
        newState.calcE();
        newState.calcP();
        newState.calcGlass();
        newState.calcKoord();
        this.setState(_.clone(newState));
    }

    /**
     * Parameetri muutmine
     */
    onChangeValue(info) {
        if (!this.validateValue(info)) return;

        let newState = this.cloneState();
        let isChanged = false;

        if (Array.isArray(this.state[info.field]) || _.isObject(this.state[info.field])) {
            // Massiivi elemendi või objekti atribuudi väärtustamine:
            newState[info.field][info.idx] = info.value;
            if (newState[info.field][info.idx] !== this.state[info.field][info.idx]) isChanged = true;
        } else {
            // Lihtelemendi väärtustamine:
            switch (info.field) {
                case 'keeratud_esimene':
                case 'keeratud_viimane':
                    if (info.value === null) info.value = false;
                    break;
                default:
                    break;
            }
            newState[info.field] = info.value;
            if (newState[info.field] !== this.state[info.field]) isChanged = true;
        }

        if (!isChanged) return;

        switch (info.field) {
            case 'ex':
            case 'ey':
                newState.calcKalle();
                break;
            case 'ez':
                newState.ez[info.idx] = info.value;
                newState.calcKalle();
                break;
            case 'pNominaal':
                newState.calcKalle();
                break;
            case 'distants':
                // TODO: Viia 603 + 160 konfi konstandiks
                let yshiftOffset = SettingsStore.getYshiftOffset();
                // newState.ynihe = Math.round(603 - info.value - ((newState.glass.maxy - newState.glass.miny) / 2)) + 160;
                newState.ynihe = Math.round(yshiftOffset - info.value - ((newState.glass.maxy - newState.glass.miny) / 2));
                newState.calcKoord();
                break;
            case 'xnihe':
            case 'ynihe':
                newState.calcKoord();
                break;
            case 'tolerants1k':
                for (let i = 0; i < newState.lineCount * 3; i++) {
                    if (i % 3 == 1) {
                        // Keskmised punktid
                        newState.pYlemineTolerants1[i] = newState.pAlumineTolerants1[i] = info.value;
                    }
                }
                break;
            case 'tolerants2k':
                for (let i = 0; i < newState.lineCount * 3; i++) {
                    if (i % 3 == 1) {
                        // Keskmised punktid
                        newState.pYlemineTolerants2[i] = newState.pAlumineTolerants2[i] = info.value;
                    }
                }
                break;
            case 'tolerants1s':
                for (let i = 0; i < newState.lineCount * 3; i++) {
                    if (i % 3 != 1) {
                        // Otsapunktid
                        newState.pYlemineTolerants1[i] = newState.pAlumineTolerants1[i] = info.value;
                    }
                }
                break;
            case 'tolerants2s':
                for (let i = 0; i < newState.lineCount * 3; i++) {
                    if (i % 3 != 1) {
                        // Otsapunktid
                        newState.pYlemineTolerants2[i] = newState.pAlumineTolerants2[i] = info.value;
                    }
                }
                break;
            case 'hudLine':
                newState.calcHudFromOffset();
                break;
            case 'anglePoints':
            default:
                break;
        }
        this.recalcPointsAngle(newState);

        this.setState(_.clone(newState));
    }

    recalcPointsAngle(newState) {
        let points = [];
        for (let i = 0; i < newState.pJoon.length; i++) {
            if (newState.anglePoints[i]) points.push(i);
        }
        if (points.length === 2) {
            // Arvuta kahe punkti vaheline nurk:
            newState.calcPointsAngle(points[0], points[1]);
        } else {
            newState.angleValue = null;
        }
    }

    /**
     * Punkti E nihutamine näidatud offsetiga (märk näitab nihutamise suunda)
     * @param {number} offset Offset (negative - left, positive - right)
     */
    onMoveE(offset) {
        // valitud_e sisaldab vastava E punkti indeksit

        let newState = this.cloneState();
        newState.moveE(this.state.valitud_e, offset);
        newState.calcKoord();
        this.recalcPointsAngle(newState);
        this.setState(_.clone(newState));
    }

    /**
     * @param {number} offset Offset (negative - down, positive - up)
     */
    onMoveP(offset) {
        let newState = this.cloneState();
        newState.moveP(this.state.valitud_p, offset);
        newState.calcKoord();
        this.recalcPointsAngle(newState);
        this.setState(_.clone(newState));
    }

    onMoveHud(offset) {
        let newState = this.cloneState();
        newState.moveHud(this.state.valitud_hud, offset);
        newState.calcOffsetHud();
        this.setState(_.clone(newState));
    }

    onRemoveP() {
        let newState = this.cloneState();
        newState.removeP(this.state.valitud_p);
        this.setState(_.clone(newState));
    }

    onAssignP() {
        let newState = this.cloneState();
        newState.assignP();
        this.setState(_.clone(newState));
    }

    createNcpFileParser(rotated, ncpFileType) {
        switch (ncpFileType) {
            case "dat":
                return new DatFileParser();
            default:
                return new NcpFileParser(rotated);
        }
    }

    validateValue(info) {
        switch (info.field) {
            case 'tolerants1k':
            case 'tolerants2k':
            case 'tolerants1s':
            case 'tolerants2s':
                if (info.value > 9.9) return false;
                if (info.value < 0) info.value = 0;
                break;
            case 's':
            case 'pNominaal':
            case 'pYlemineTolerants1':
            case 'pYlemineTolerants2':
            case 'pAlumineTolerants1':
            case 'pAlumineTolerants2':
            case 'hudNominaal':
            case 'hudYlemineTolerants1':
            case 'hudYlemineTolerants2':
            case 'hudAlumineTolerants1':
            case 'hudAlumineTolerants2':
                if (info.value < 0) return false;
                break;
            case 'hudLine':
                if (info.value == null || info.value == "") return true;
                if (info.value < 1 || info.value > this.state.lineCount) return false;
                break;
            default:
                break;
        }
        return true;
    }

}

export default (alt.createStore(ProductStore, 'ProductStore'): AltStore<ProductInfo>);
